import {
  SurveyCaption,
  SurveysResponsesQuestionsQuery,
  SurveysResponsesQuestionsQueryVariables,
} from '@/graphql/generated/graphql';
import { FETCH_SURVEY_RESPONSE_QUESTIONS } from '@/graphql/queries/survey/survey-dashboard';
import { useQuery } from '@apollo/client';
import { Answer } from '../likert-chart';
import Button from '@/components/Button';
import { useSurveyDashboardStore } from '../store/useSurveyDashboard.store';
import { utils as xlsxUtils, write as xlsxWrite } from 'xlsx';
import { useEffect } from 'react';
import { classNames } from '@/utils/class-names';

export const ResponseTable = () => {
  const { updateCaptions, selectedSchoolsIds, currentSurvey, captions, updateIsEmpty, currentTab, page, isEmpty } =
    useSurveyDashboardStore((store) => {
      return {
        isEmpty: store.isEmpty,
        updateCaptions: store.updateCaptions,
        selectedSchoolsIds: store.selectedSchoolsIds,
        currentSurvey: store.currentSurvey,
        captions: store.captions,
        updateIsEmpty: store.updateIsEmpty,
        currentTab: store.currentTab,
        page: store.page,
      };
    });

  const { data, loading } = useQuery<SurveysResponsesQuestionsQuery, SurveysResponsesQuestionsQueryVariables>(
    FETCH_SURVEY_RESPONSE_QUESTIONS,
    {
      variables: {
        surveyId: currentSurvey?.id ? Number(currentSurvey.id) : 0,
        schoolIds: selectedSchoolsIds.length ? selectedSchoolsIds.map(Number) : null,
      },
      fetchPolicy: 'cache-first',
    },
  );

  useEffect(() => {
    if (currentTab === 'response-table') {
      updateCaptions(data?.surveysResponsesQuestions?.captions as SurveyCaption[]);
      updateIsEmpty((data?.surveysResponsesQuestions?.data?.length || 0) === 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const questions = data?.surveysResponsesQuestions?.data;

  const [captionNegative2, captionNegative1, captionNeutral, captionPositive1, captionPositive2] = captions || [];

  const handleDownload = () => {
    if (!questions) return;

    // Format data for Excel
    const excelData = questions.map((question) => {
      const [negative2, negative1, neutral, positive1, positive2] = question?.answers as Answer[];

      return {
        [page?.tableResponses.category || '']: question?.measure ?? '',
        [page?.tableResponses.date || '']: question?.date ? new Date(question.date).toLocaleDateString('en-GB') : '',
        [page?.tableResponses.question || '']: question?.question,
        [page?.tableResponses.total || '']: question?.responses,
        [captionNegative2?.label ?? '']: negative2.answer.value,
        [captionNegative1.label ?? '']: negative1.answer.value,
        [captionNeutral.label ?? '']: neutral.answer.value,
        [captionPositive1.label ?? '']: positive1.answer.value,
        [captionPositive2.label ?? '']: positive2.answer.value,
      };
    });

    // Create worksheet
    const worksheet = xlsxUtils.json_to_sheet(excelData);

    // Create workbook
    const workbook = {
      Sheets: { 'Survey Responses': worksheet },
      SheetNames: ['Survey Responses'],
    };

    // Generate Excel file
    const excelBuffer = xlsxWrite(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    // Create Blob and download
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Create download link
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;

    // Get current date for filename
    const today = new Date().toISOString().split('T')[0];
    link.download = `survey_responses_${today}.xlsx`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  console.log({ isEmpty, loading });
  if (isEmpty && !loading) {
    return (
      <div className=" col-span-12 text-center">
        <h1 className=" !text-sh-md !font-bold">{page?.notFoundMessage}</h1>
      </div>
    );
  }

  return (
    <div className="col-span-12 overflow-x-auto overflow-y-hidden">
      <div className="min-w-[1000px] divide-y">
        <table className="min-w-full">
          <colgroup>
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-3/12" />
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-1/12" />
          </colgroup>
          <thead>
            <tr className="text-sh-xxxs font-bold text-black-100">
              <th className="px-4 py-2" scope="col">
                {page?.tableResponses.category}
              </th>
              <th className="px-4 py-2" scope="col">
                {page?.tableResponses.date}
              </th>
              <th className="px-4 py-2" scope="col">
                {page?.tableResponses.question}
              </th>
              <th className="px-4 py-2" scope="col">
                {page?.tableResponses.total}
              </th>
              <th
                className={classNames('px-4 py-2')}
                style={{ backgroundColor: `${captionNegative2?.color}30` }}
                scope="col">
                {captionNegative2?.label}
              </th>
              <th
                className={classNames('px-4 py-2')}
                style={{ backgroundColor: `${captionNegative1?.color}30` }}
                scope="col">
                {captionNegative1?.label}
              </th>
              <th
                className={classNames('px-4 py-2')}
                style={{ backgroundColor: `${captionNeutral?.color}30` }}
                scope="col">
                {captionNeutral?.label}
              </th>
              <th
                className={classNames('px-4 py-2')}
                style={{ backgroundColor: `${captionPositive1?.color}30` }}
                scope="col">
                {captionPositive1?.label}
              </th>
              <th
                className={classNames('px-4 py-2')}
                style={{ backgroundColor: `${captionPositive2?.color}30` }}
                scope="col">
                {captionPositive2?.label}
              </th>
            </tr>
          </thead>
          <tbody className="text-sh-xxxs text-black-100">
            {loading || !questions
              ? // Render 3 skeleton rows during loading
                Array.from({ length: 3 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse border-b border-[#89B4FD80]">
                    {Array.from({ length: 9 }).map((_, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-2 text-center">
                        <div className="h-4 rounded bg-gray-300"></div>
                      </td>
                    ))}
                  </tr>
                ))
              : questions.map((question, i) => {
                  const dateDayAndMonth = question?.date ? new Date(question?.date).toLocaleDateString('en-GB') : '';
                  const [negative2, negative1, neutral, positive1, positive2] = question?.answers as Answer[];
                  return (
                    <tr key={i} className="border-b border-[#89B4FD80] last:border-b-0">
                      {/* Category (dateMonthName) */}
                      <td className="px-4 py-2 text-center">{question?.measure}</td>
                      {/* Date */}
                      <td className="px-4 py-2 text-center">{dateDayAndMonth}</td>
                      {/* Question */}
                      <td className="px-4 py-2 text-center">{question?.question}</td>
                      {/* Total responses */}
                      <td className="px-4 py-2 text-center font-bold">{question?.responses}</td>
                      {/* Strongly disagree */}
                      <td
                        className={classNames('px-4 py-2 text-center')}
                        style={{ backgroundColor: `${captionNegative2?.color}30` }}>
                        {negative2.answer.value}
                      </td>
                      {/* Disagree */}
                      <td
                        className={classNames('px-4 py-2 text-center')}
                        style={{ backgroundColor: `${captionNegative1?.color}30` }}>
                        {negative1.answer.value}
                      </td>
                      {/* Neutral */}
                      <td
                        className={classNames('px-4 py-2 text-center')}
                        style={{ backgroundColor: `${captionNeutral?.color}30` }}>
                        {neutral.answer.value}
                      </td>
                      {/* Agree */}
                      <td
                        className={classNames('px-4 py-2 text-center')}
                        style={{ backgroundColor: `${captionPositive1?.color}30` }}>
                        {positive1.answer.value}
                      </td>
                      {/* Strongly agree */}
                      <td
                        className={classNames('px-4 py-2 text-center')}
                        style={{ backgroundColor: `${captionPositive2?.color}30` }}>
                        {positive2.answer.value}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>

        <Button className="mb-sh-30 mt-sh-60" onClick={handleDownload} disabled={loading || !questions}>
          {page?.downloadButton.text}
        </Button>
      </div>
    </div>
  );
};
